import React, {useMemo, useState} from 'react';
import { useParams } from "react-router-dom";
import { useGame } from "../../api/api";
import { useMediaQuery } from "react-responsive";
import { MOBILE, UPLOADHOST } from "../../constants/constants";
import { Helmet } from "react-helmet";
import Preloader from "../../components/Preloader";
import Error from "../404/Error";
import InnerPage from "../../components/InnerPage";
import Container from "../../components/Container";
import StoreLink from "../../components/StoreLink/StoreLink";
import PlayButton from "../../components/GameBlock/PlayButton";
import SliderButton from "../../components/SliderButton";
import CardSlider from "../../components/CardSlider";
import Card from "../../components/Card";
import ReactPlayer from "react-player";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import styles from './game.module.scss';
import {useTranslation} from "react-i18next";
import Page from "../../components/Page";

const Game = ({ lenisRef }) => {
  const { gameId } = useParams();
  const [play, setPlay] = useState(false);
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const { gameData, isGameLoading, gameError } = useGame(gameId, lang);

  const isMobile = useMediaQuery({ maxDeviceWidth: MOBILE });

  const onBtnClick = () => {
    setPlay(!play)
  }

  if (isGameLoading) {
    return <Preloader />
  }

  if (gameError) {
    return <Error />
  }

  const data = gameData?.data || null;

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio | {data?.title}</title>
      </Helmet>
      <InnerPage lenisRef={lenisRef} className={styles.wrapper}>
        <div className={styles.top}>
          { data.background_top &&
            <div className={styles.backImg}>
              <img src={`${UPLOADHOST}${data.background_top.url}`} alt={'main game img'} />
            </div>
          }
          <Container>
            <div className={`${styles.main} text-center`}>
              { data.icon &&
                <div className={styles.icon}>
                  <img src={`${UPLOADHOST}${data.icon.url}`} alt={'icon'} />
                </div>
              }
              <h1 className={styles.title}>{data.title}</h1>
              <p className={`${styles.description} text-pink`}>{data.description}</p>
              { data.stores.show &&
                <div className={styles.storeButtons}>
                  <StoreLink store={'appStore'} to={data.stores.appstore} />
                  <StoreLink store={'googlePlay'} to={data.stores.googlestore} />
                </div>
              }
            </div>
          </Container>
        </div>

        { data.video_block?.show &&
          <Container className={styles.secBlock}>
            <div className="row flex-col-center">
              <div className="col-lg-10">
                { data.stores?.show &&
                  <div className={styles.secStoreBlock}>
                    <div className={styles.secStoreIcon}>
                      { data.icon &&
                        <img src={`${UPLOADHOST}${data.icon.url}`} alt={'game icon'}/>
                      }
                    </div>
                    <h3 className={styles.secStoreTitle}>{data.title}</h3>
                    <div className={styles.secStoreButtons}>
                      <StoreLink store={'appStore'} to={data.stores.appstore} />
                      <StoreLink store={'googlePlay'} to={data.stores.googlestore} />
                    </div>
                  </div>
                }
                { data.video_block.youtube &&
                  <div className={styles.videoBlock}>
                    <ReactPlayer
                      controls={false}
                      playing={play}
                      width={'100%'}
                      height={'100%'}
                      url={data.video_block.youtube}
                      playIcon={<PlayButton onClick={onBtnClick}/>}
                      light={true}
                    />
                  </div>
                }
              </div>
            </div>

            <div className="row flex-col-center">
              <div className="col-lg-8">
                <div className="row">
                  <div className="col-lg-6">
                    <h3 className="text-ellipsis">{data.video_block.title}</h3>
                  </div>
                  <div className="col-lg-6">
                    <p className={'text-pink'}>{data.video_block.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        }

        { data.slider?.show && data.slider.images &&
          <div className={styles.slider}>
            <Carousel
              draggable={true}
              showDots={false}
              infinite={true}
              responsive={
                {
                  wide: { breakpoint: { max: 10000, min: 1200 }, items: 5 },
                  tabletLG: { breakpoint: { max: 1200, min: 768 }, items: 4 },
                  tablet: { breakpoint: { max: 768, min: 674 }, items: 3 },
                  mobileLG: { breakpoint: { max: 674, min: 396 }, items: 3 },
                  mobile: { breakpoint: { max: 396, min: 0 }, items: 1 },
                }
              }
              slidesToSlide={1}
              centerMode={isMobile}
              arrows={true}
              customLeftArrow={<SliderButton/>}
              customRightArrow={<SliderButton direction={'next'}/>}
              itemClass={styles.smSlideWrap}
            >
              {
                data.slider.images.map((slide) => {
                  return (
                    <div key={slide.id} className={styles.slide}>
                      <img src={`${UPLOADHOST}${slide.url}`} alt={`slide_${slide.id}`}/>
                    </div>
                  );
                })
              }
            </Carousel>
          </div>
        }

        { data.two_images_block.show && data.two_images_block.images &&
          <Container className={styles.cardsBlock1}>
            <div className="row flex-col-center">
              <div className="col-xl-10 col-lg-11 col-md-12">
                <div className={`row flex-col-center-h ${styles.cardSliderCol}`}>
                  <div className="col-lg-6 col-xl-6 col-md-6 col-sm-12">
                    <div className={styles.cardsInfo}>
                      <h4 className={styles.cardsTitle}>{data.two_images_block.title}</h4>
                      <p className="text-pink">{data.two_images_block.description}</p>
                    </div>
                  </div>
                  <div className="col-lg-6 col-xl-6 col-md-6 col-sm-12">
                    <CardSlider data={data.two_images_block.images} direction={'left'}/>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        }

        { data.central_screen.show &&
          <div className={styles.gameDescBlock}>
            <div className={`${styles.backImg} ${styles.gameDescBackImg}`}>
              { data.central_screen.background && <img src={`${UPLOADHOST}${data.central_screen.background.url}`} alt={'game img'} /> }
            </div>
            <Container className={styles.gameDesc}>
              <div className={`row flex-col-center`}>
                <div className="col-xl-10 col-lg-10 col-md-12 d-flex">
                  <div className="row flex-col-center-h flex-grow">
                    <div className="col-lg-8 col-md-7 col-sm-12 col-xs-12">
                      <div className={styles.gameDescWrap}>
                        <h4 className={styles.gameDescTitle}>{data.central_screen.block.title}</h4>
                        <p className={styles.gameDescText}>{data.central_screen.block.description}</p>
                        <div className={styles.gameDescImg}>
                          { data.central_screen.block.image &&
                            <img src={`${UPLOADHOST}${data.central_screen.block.image.url}`} alt={'game desc img'} />
                          }
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-5 col-sm-12 col-xs-12">
                      <Card
                        color={'white'}
                        className={styles.gameDescCard}
                        img={`${UPLOADHOST}${data.central_screen.card.image.url}`}
                        text={data.central_screen.card.title}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </div>
        }

        { data.two_images_block_2.show && data.two_images_block_2.images &&
          <Container className={styles.cardsBlock2}>
            <div className="row flex-col-center">
              <div className="col-xl-10 col-lg-11 col-md-12">
                <div className={`row flex-col-center-h ${styles.cardSliderCol}`}>
                  <div className="col-lg-6 col-xl-6 col-md-6 col-sm-12">
                    <CardSlider data={data.two_images_block_2.images} direction={'right'}/>
                  </div>
                  <div className="col-lg-6 col-xl-6 col-md-6 col-sm-12">
                    <div className={styles.cardsInfo}>
                      <h4 className={styles.cardsTitle}>{data.two_images_block_2.title}</h4>
                      <p className="text-pink">{data.two_images_block_2.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        }

        {
          data.background_bottom &&
            <div className={styles.bottom}>
              <div className={`${styles.backImg} ${styles.bottomBackImg}`}>
                <img src={`${UPLOADHOST}${data.background_bottom.url}`} alt={'bottom img'} />
              </div>
              <Container className={styles.bottomContainer}>
                <div className={`${styles.main} text-center`}>
                  { data.icon &&
                    <div className={styles.icon}>
                      <img src={`${UPLOADHOST}${data.icon.url}`} alt={'icon'} />
                    </div>
                  }
                  <h1 className={styles.title}>{data.title}</h1>
                  { data.stores.show &&
                    <div className={styles.storeButtons}>
                      <StoreLink store={'appStore'} to={data.stores.appstore} />
                      <StoreLink store={'googlePlay'} to={data.stores.googlestore} />
                    </div>
                  }
                </div>
              </Container>
            </div>
        }
      </InnerPage>
    </Page>
  );
};

export default Game;